/**
 * Bruce › Agents — the weekly review screen from the Bruce Agent Planner.
 *
 * One card per registry agent (status, owner, 28-day scorecard, health) plus
 * the "nobody covers these" list: questions Bruce could not answer, which is
 * the demand signal for what to build next. Everything is read from the
 * backend; nothing is compiled here. Clicking a sample question asks it.
 */
import { Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'

import { fetchBruceAgents } from 'api'
import { radius, space } from 'app/theme/tokens'

const NAVY = '#282E3C'
const ORANGE = '#F25E35'
const CREAM = '#FBF5ED'

const STATUS_LABEL = {
  proposed: 'Proposed',
  building: 'Building',
  pilot: 'Pilot',
  live: 'Live',
  watch: 'Watch',
  retired: 'Retired'
}

const HEALTH = {
  green: { color: '#2F7D5B', label: 'Healthy' },
  red: { color: '#B23A3A', label: 'Needs attention' },
  quiet: { color: '#9A8F7C', label: 'Too early to judge' }
}

const useStyles = makeStyles(() => ({
  scroll: {
    flex: 1,
    overflowY: 'auto',
    padding: space.md,
    background: '#FBF9F4',
    display: 'flex',
    flexDirection: 'column',
    gap: space.sm
  },
  intro: {
    fontSize: 11,
    color: '#9A8F7C',
    lineHeight: 1.4
  },
  previewRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#FFF',
    border: '1px solid #EDE6D8',
    borderRadius: radius.md,
    padding: `${space.sm} 12px`
  },
  previewTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#282E3C'
  },
  previewNote: {
    fontSize: 10.5,
    color: '#9A8F7C',
    lineHeight: 1.4,
    marginTop: 2
  },
  previewBtn: {
    marginLeft: 'auto',
    flexShrink: 0,
    background: '#FBF5ED',
    border: '1px solid #F25E35',
    color: '#F25E35',
    borderRadius: radius.sm,
    fontSize: 11,
    fontWeight: 600,
    padding: '5px 12px',
    cursor: 'pointer'
  },
  card: {
    background: '#FFF',
    border: '1px solid #EDE6D8',
    borderRadius: radius.md,
    padding: `${space.sm} 12px`,
    display: 'flex',
    flexDirection: 'column',
    gap: 6
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: space.sm
  },
  name: { fontSize: 13, fontWeight: 800, color: NAVY, flex: 1 },
  pill: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
    borderRadius: radius.pill,
    padding: '1px 7px',
    background: '#E4EDF7',
    color: '#3A6EA5'
  },
  pillLive: { background: '#E3F1E9', color: '#2F7D5B' },
  pillWatch: { background: '#F6E3E3', color: '#B23A3A' },
  pillMuted: { background: '#ECE8E1', color: '#8A857E' },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  owner: { fontSize: 11, color: '#9A8F7C' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4
  },
  metric: {
    background: CREAM,
    borderRadius: radius.sm,
    padding: '4px 6px',
    minWidth: 0
  },
  metricK: {
    fontSize: 9,
    color: '#9A8F7C',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    whiteSpace: 'nowrap'
  },
  metricV: {
    fontSize: 13,
    fontWeight: 700,
    color: NAVY,
    fontVariantNumeric: 'tabular-nums'
  },
  reason: { fontSize: 11, color: '#B23A3A' },
  golden: { fontSize: 11, color: '#6E6862' },
  goldenBad: { color: '#B23A3A' },
  goldenWhy: {
    fontSize: 10.5,
    color: '#8A8272',
    lineHeight: 1.45,
    marginTop: 2,
    paddingLeft: 8,
    borderLeft: '2px solid #EFE6D6'
  },
  house: {
    background: '#FFF',
    border: '1px solid #EDE5D5',
    borderRadius: 10,
    padding: '10px 13px',
    marginBottom: 12
  },
  houseTitle: { fontSize: 12, fontWeight: 700, marginBottom: 2 },
  sampleRow: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  sample: {
    fontSize: 11,
    color: NAVY,
    background: 'transparent',
    border: '1px solid #EDE6D8',
    borderRadius: radius.pill,
    padding: '2px 8px',
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': { borderColor: ORANGE, color: ORANGE }
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 800,
    color: NAVY,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: space.xs
  },
  unanswered: {
    background: '#FFF',
    border: '1px solid #EDE6D8',
    borderRadius: radius.md,
    padding: `6px 12px`,
    fontSize: 12,
    color: NAVY
  },
  unansweredNote: { fontSize: 10.5, color: '#9A8F7C' },
  empty: { fontSize: 12, color: '#9A8F7C' }
}))

const pct = (share) =>
  share === null || share === undefined ? '—' : `${Math.round(share * 100)}%`

const seconds = (ms) => (ms ? `${Math.round(ms / 1000)} s` : '—')

const goldenLine = (golden) => {
  if (!golden) return 'Golden questions: not run yet'
  const when = golden.run_at
    ? new Date(golden.run_at).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      })
    : ''
  return `Golden questions: ${golden.passed}/${golden.asked} passed${
    when ? ` · ${when}` : ''
  }`
}

// A red number says "something broke"; this says what. The eval reports the
// assertion that failed (max_words, must_not_include, routing…) — without it
// someone has to re-ask the question by hand to find out.
const failedLines = (golden, classes) =>
  golden && golden.failed && golden.failed.length > 0 ? (
    <div className={classes.goldenWhy}>
      {golden.failed.slice(0, 3).map((f) => (
        <div key={f.question}>
          {f.question} — {f.assertion || f.resolution}
        </div>
      ))}
    </div>
  ) : null

const pillClass = (classes, status) => {
  if (status === 'live') return `${classes.pill} ${classes.pillLive}`
  if (status === 'watch') return `${classes.pill} ${classes.pillWatch}`
  if (status === 'proposed' || status === 'retired' || status === 'building') {
    return `${classes.pill} ${classes.pillMuted}`
  }
  return classes.pill
}

const AgentCard = ({ agent, onAsk }) => {
  const classes = useStyles()
  const m = agent.month || {}
  const w = agent.week || {}
  const health = HEALTH[agent.health] || HEALTH.quiet
  return (
    <div className={classes.card}>
      <div className={classes.cardTop}>
        <Tooltip title={health.label}>
          <span className={classes.dot} style={{ background: health.color }} />
        </Tooltip>
        <span className={classes.name}>{agent.label}</span>
        <span className={pillClass(classes, agent.status)}>
          {STATUS_LABEL[agent.status] || agent.status}
        </span>
      </div>
      <div className={classes.owner}>
        {agent.owner ? `Owner: ${agent.owner}` : 'Owner not set'}
        {' · '}
        {w.asks || 0} asked this week
      </div>
      <div className={classes.grid}>
        <div className={classes.metric}>
          <div className={classes.metricK}>Asks 28d</div>
          <div className={classes.metricV}>{m.asks || 0}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>👍 of rated</div>
          <div className={classes.metricV}>{pct(m.up_share)}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>Couldn&apos;t</div>
          <div className={classes.metricV}>{m.declined || 0}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>Errors</div>
          <div className={classes.metricV}>{m.errors || 0}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>Unrated</div>
          <div className={classes.metricV}>{m.unrated || 0}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>👎</div>
          <div className={classes.metricV}>{m.down || 0}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>Wait p50</div>
          <div className={classes.metricV}>{seconds(m.latency_p50_ms)}</div>
        </div>
        <div className={classes.metric}>
          <div className={classes.metricK}>Tokens avg</div>
          <div className={classes.metricV}>
            {m.tokens_avg ? `${(m.tokens_avg / 1000).toFixed(1)}k` : '—'}
          </div>
        </div>
      </div>
      {(agent.health_reasons || []).map((r) => (
        <div key={r} className={classes.reason}>
          {r}
        </div>
      ))}
      <div
        className={`${classes.golden} ${
          agent.golden && agent.golden.passed < agent.golden.asked
            ? classes.goldenBad
            : ''
        }`}
      >
        {goldenLine(agent.golden)}
      </div>
      {failedLines(agent.golden, classes)}
      {agent.sample_questions && agent.sample_questions.length > 0 && (
        <div className={classes.sampleRow}>
          {agent.sample_questions.slice(0, 3).map((q) => (
            <button
              key={q}
              type="button"
              className={classes.sample}
              onClick={() => onAsk(q)}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

AgentCard.propTypes = {
  agent: PropTypes.object.isRequired,
  onAsk: PropTypes.func.isRequired
}

const BruceAgentsPanel = ({ onAsk, onPreviewAsUser }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    fetchBruceAgents()
      .then((d) => {
        if (alive) setData(d)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })
    return () => {
      alive = false
    }
  }, [])

  if (failed) {
    return (
      <div className={classes.scroll}>
        <div className={classes.empty}>
          Couldn&apos;t load the agents right now — try again in a moment.
        </div>
      </div>
    )
  }
  if (!data) {
    return (
      <div className={classes.scroll}>
        <div className={classes.empty}>Loading agents…</div>
      </div>
    )
  }

  const none = data.no_agent || {}
  const noneMonth = none.month || {}
  const recent = none.recent || []
  const tutorials = data.tutorial_requests || {}
  const tutorialTopics = tutorials.topics || []

  return (
    <div className={classes.scroll}>
      <div className={classes.intro}>
        Every agent Bruce has, with its last {data.window_days} days from the
        answer log. Green is healthy, red needs attention, grey is too early to
        judge. Tap a sample question to ask it.
      </div>
      {data.house_golden && (
        <div className={classes.house}>
          <div className={classes.houseTitle}>House rules</div>
          <div
            className={`${classes.golden} ${
              data.house_golden.passed < data.house_golden.asked
                ? classes.goldenBad
                : ''
            }`}
          >
            {goldenLine(data.house_golden)} — what Bruce says when it cannot
            help, and what it never says about how it is built.
          </div>
          {failedLines(data.house_golden, classes)}
        </div>
      )}
      {onPreviewAsUser && (
        <div className={classes.previewRow}>
          <div>
            <div className={classes.previewTitle}>
              See it as a centre team does
            </div>
            <div className={classes.previewNote}>
              Hides this page and the way in. Answers do not change — they were
              already scoped to you.
            </div>
          </div>
          <button
            type="button"
            className={classes.previewBtn}
            onClick={onPreviewAsUser}
          >
            Preview
          </button>
        </div>
      )}
      {(data.agents || []).map((agent) => (
        <AgentCard key={agent.key} agent={agent} onAsk={onAsk} />
      ))}
      <div className={classes.sectionTitle}>
        Nobody covers these · {noneMonth.asks || 0} in {data.window_days} days
      </div>
      {recent.length === 0 ? (
        <div className={classes.empty}>
          Every question in this window found an agent.
        </div>
      ) : (
        recent.map((r) => (
          <div key={`${r.when}-${r.question}`} className={classes.unanswered}>
            {r.question || '(question not found)'}
            <div className={classes.unansweredNote}>
              {r.resolution === 'no_agent' ? 'No agent' : "Couldn't answer"}
              {r.note ? ` · ${r.note}` : ''}
            </div>
          </div>
        ))
      )}
      <div className={classes.sectionTitle}>
        Tutorials requested · {tutorials.open || 0} open
      </div>
      {tutorialTopics.length === 0 ? (
        <div className={classes.empty}>No missing tutorials recorded yet.</div>
      ) : (
        tutorialTopics.map((r) => (
          <div key={r.topic} className={classes.unanswered}>
            {r.topic}
            <div className={classes.unansweredNote}>
              {r.asks} {r.asks === 1 ? 'ask' : 'asks'}
              {r.screen_key ? ` · ${r.screen_key}` : ''}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

BruceAgentsPanel.propTypes = {
  onAsk: PropTypes.func.isRequired,
  onPreviewAsUser: PropTypes.func
}

BruceAgentsPanel.defaultProps = {
  onPreviewAsUser: null
}

export default BruceAgentsPanel
